import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../api';
import Estado from '../components/ui/Estado';
import EmptyState from '../components/ui/EmptyState';
import PageHeader from '../components/ui/PageHeader';
import Icon from '../components/ui/Icon';
import { useConfirm } from '../hooks/useConfirm';
import { toast } from '../components/Toast';

const SITE = 'https://www.agrigest.com.br';

/** Linha de usuário. Antes cada bloco repetia 8 propriedades de estilo inline. */
function LinhaUsuario({ usuario, destaque, esmaecido, acoes }) {
  const classes = [
    'linha-lista',
    'linha-usuario',
    destaque ? 'linha-usuario--destaque' : '',
    esmaecido ? 'linha-usuario--esmaecida' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      <div className="item-conteudo">
        <div className="item-titulo">{usuario.nome}</div>
        {usuario.email && <div className="item-meta item-meta--quebra">{usuario.email}</div>}
      </div>
      <div className="item-acoes">{acoes}</div>
    </div>
  );
}

/**
 * Passo a passo de convite.
 *
 * O convite depende de uma informação que só o administrador tem — o código da
 * fazenda — e de uma sequência que não é óbvia: a pessoa se cadastra sozinha e
 * só depois é aprovada aqui. Sem essa explicação, o caminho natural era procurar
 * um botão "adicionar usuário" que não existe.
 */
function TutorialConvite() {
  const [codigo, setCodigo] = useState(() => localStorage.getItem('codigoConvite') || '');
  const [copiado, setCopiado] = useState(false);
  const timer = useRef(null);
  const fazenda = localStorage.getItem('fazenda') || 'sua fazenda';

  // Sessões abertas antes deste código existir não têm o convite no
  // localStorage; nesse caso buscamos para o passo 1 não ficar vazio.
  useEffect(() => {
    if (codigo) return;
    let vivo = true;
    api.getFazendaMe()
      .then(f => {
        if (!vivo || !f?.codigoConvite) return;
        localStorage.setItem('codigoConvite', f.codigoConvite);
        setCodigo(f.codigoConvite);
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [codigo]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copiar = async () => {
    try {
      // clipboard exige contexto seguro; em HTTP o catch avisa em vez de falhar calado.
      await navigator.clipboard.writeText(codigo);
      setCopiado(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopiado(false), 2000);
    } catch {
      toast('Não foi possível copiar automaticamente. Anote o código.', 'error');
    }
  };

  const mensagem = encodeURIComponent(
    `Vem usar o AgriGest comigo para controlar a ${fazenda}.\n\n` +
    `1. Acesse ${SITE}\n` +
    '2. Toque em "Entrar em fazenda existente"\n' +
    `3. Use o código de acesso: ${codigo}\n\n` +
    'Depois eu aprovo seu acesso por aqui.'
  );

  return (
    <div className="card">
      <div className="secao-titulo">
        <h2>Como convidar alguém para a fazenda</h2>
      </div>
      <p className="secao-sub">
        Não existe um botão para criar usuário: a pessoa se cadastra sozinha usando o
        código da fazenda, e você libera o acesso aqui. Assim cada um tem a própria senha.
      </p>

      <ol className="passos">
        <li className="passo">
          <div className="passo-titulo">Copie o código de acesso da fazenda</div>
          <div className="passo-texto">
            É o código que identifica a <strong>{fazenda}</strong>. Ele também fica
            sempre visível no menu, então você não precisa decorar.
          </div>
          {codigo ? (
            <div className="codigo-box">
              <span className="codigo-valor">{codigo}</span>
              <button type="button" className="btn-primary btn-sm" onClick={copiar}>
                <Icon nome={copiado ? 'ok' : 'copiar'} tamanho={14} />
                {copiado ? 'Copiado' : 'Copiar código'}
              </button>
            </div>
          ) : (
            <div className="aviso aviso--info">
              Abra o menu para ver o código de acesso da fazenda.
            </div>
          )}
        </li>

        <li className="passo">
          <div className="passo-titulo">Envie o código para a pessoa</div>
          <div className="passo-texto">
            Pode ser por WhatsApp, mensagem ou pessoalmente. Envie para quem você confia:
            quem entra com este código consegue solicitar acesso à fazenda.
          </div>
          {codigo && (
            <a
              href={`https://wa.me/?text=${mensagem}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp btn-sm"
            >
              <Icon nome="suporte" tamanho={15} />
              Enviar convite pelo WhatsApp
            </a>
          )}
        </li>

        <li className="passo">
          <div className="passo-titulo">A pessoa se cadastra com o código</div>
          <div className="passo-texto">
            Na tela de entrada do AgriGest ela toca em <strong>Entrar em fazenda
            existente</strong>, informa o código, o nome de usuário, o email, cria uma
            senha e toca em <strong>Solicitar acesso</strong>.
          </div>
        </li>

        <li className="passo">
          <div className="passo-titulo">Você aprova o acesso aqui</div>
          <div className="passo-texto">
            O pedido aparece nesta página em <strong>Aguardando aprovação</strong>, no
            topo. Enquanto você não aprovar, a pessoa não vê nenhum dado da fazenda.
            Depois de aprovada, ela entra informando o nome de usuário, a senha e o nome
            da fazenda (<strong>{fazenda}</strong>).
          </div>
        </li>
      </ol>

      <div className="aviso aviso--info">
        <strong>Você continua no controle.</strong> Pode remover o acesso de qualquer
        pessoa quando quiser, e o histórico do que ela lançou permanece. Se o código
        chegar a quem não devia, o pedido de acesso ainda depende da sua aprovação.
      </div>
    </div>
  );
}

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const { confirmar, ConfirmDialog } = useConfirm();

  // O carregar anterior não tinha try/catch: se a API falhasse, setLoading(false)
  // nunca rodava e a tela ficava presa em "Carregando..." para sempre.
  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      setUsuarios(await api.getUsuarios() || []);
    } catch (err) {
      setErro(err.message || 'Não foi possível carregar os usuários.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const aprovar = async (u) => {
    try {
      await api.aprovarUsuario(u.id);
      toast(`${u.nome} agora tem acesso.`, 'success');
      carregar();
    } catch (err) { toast(err.message, 'error'); }
  };

  const remover = async (u, rotulo) => {
    const ok = await confirmar({
      titulo: `${rotulo} usuário`,
      mensagem: `${rotulo} "${u.nome}" da fazenda?`,
      aviso: 'O histórico de lançamentos feitos por esta pessoa é preservado.',
      labelConfirmar: rotulo,
    });
    if (!ok) return;
    try {
      await api.excluirUsuario(u.id);
      toast(`${u.nome} foi removido.`, 'success');
      carregar();
    } catch (err) { toast(err.message, 'error'); }
  };

  const pendentes = usuarios.filter(u => u.status === 'PENDENTE');
  const ativos = usuarios.filter(u => u.status === 'ATIVO');
  const inativos = usuarios.filter(u => u.status === 'INATIVO');

  return (
    <div>
      {ConfirmDialog}

      <PageHeader
        titulo="Usuários"
        icone="usuarios"
        descricao="Quem tem acesso ao sistema e pode registrar informações da fazenda."
      />

      <div className="card">
        <div className="secao-titulo">
          <h2>Membros da fazenda</h2>
          <span className="contador">{ativos.length} ativo{ativos.length === 1 ? '' : 's'}</span>
        </div>
        <p className="secao-sub">
          Os nomes também aparecem nos campos <strong>Adquirido em nome de</strong> (compras) e
          <strong> Depositado em nome de</strong> (produção), o que ajuda a separar
          despesas por CPF na hora do imposto de renda.
        </p>

        <Estado
          carregando={carregando}
          erro={erro}
          aoTentarNovamente={carregar}
          itens={3}
        >
          <>
            {pendentes.length > 0 && (
              <section className="grupo-usuarios">
                <h3 className="subsecao">Aguardando aprovação ({pendentes.length})</h3>
                <div className="lista-simples">
                  {pendentes.map(u => (
                    <LinhaUsuario key={u.id} usuario={u} destaque acoes={<>
                      <button className="btn-primary btn-sm" onClick={() => aprovar(u)}>Aprovar</button>
                      <button className="btn-ghost btn-ghost--perigo btn-sm" onClick={() => remover(u, 'Recusar')}>
                        Recusar
                      </button>
                    </>} />
                  ))}
                </div>
              </section>
            )}

            <section className="grupo-usuarios">
              <h3 className="subsecao">Membros ativos ({ativos.length})</h3>
              {ativos.length === 0 ? (
                <EmptyState
                  compacto
                  icone="usuarios"
                  titulo="Você é o único usuário"
                  descricao="Para dar acesso a alguém da família, a um sócio ou funcionário, siga o passo a passo logo abaixo."
                />
              ) : (
                <div className="lista-simples">
                  {ativos.map(u => (
                    <LinhaUsuario key={u.id} usuario={u} acoes={
                      u.role === 'ADMIN'
                        ? <span className="selo selo--neutro">Admin</span>
                        : <button className="btn-ghost btn-ghost--perigo btn-sm" onClick={() => remover(u, 'Remover')}>
                            Remover
                          </button>
                    } />
                  ))}
                </div>
              )}
            </section>

            {inativos.length > 0 && (
              <section className="grupo-usuarios">
                <h3 className="subsecao">Inativos ({inativos.length})</h3>
                <div className="lista-simples">
                  {inativos.map(u => (
                    <LinhaUsuario key={u.id} usuario={u} esmaecido acoes={
                      <button className="btn-secondary btn-sm" onClick={() => aprovar(u)}>Reativar</button>
                    } />
                  ))}
                </div>
              </section>
            )}
          </>
        </Estado>
      </div>

      <TutorialConvite />
    </div>
  );
}
